/**
 * Settings persistence + change notification. Same defensive-read policy as the
 * score repository: a broken value must degrade to the default, never throw.
 */

import {
  DEFAULT_SETTINGS,
  SETTINGS_STORAGE_KEY,
  type GameSettings,
} from '../config/settings';

type Listener = (settings: GameSettings) => void;

export class SettingsStore {
  private current: GameSettings;
  private readonly listeners = new Set<Listener>();

  constructor(private readonly key = SETTINGS_STORAGE_KEY) {
    this.current = { ...DEFAULT_SETTINGS, ...this.read() };
  }

  get values(): GameSettings {
    return this.current;
  }

  update(patch: Partial<GameSettings>): void {
    this.current = { ...this.current, ...patch };
    this.write();
    for (const listener of this.listeners) listener(this.current);
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private read(): Partial<GameSettings> {
    try {
      const raw = globalThis.localStorage?.getItem(this.key);
      if (!raw) return {};
      const parsed: unknown = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? (parsed as Partial<GameSettings>) : {};
    } catch {
      return {};
    }
  }

  private write(): void {
    try {
      globalThis.localStorage?.setItem(this.key, JSON.stringify(this.current));
    } catch {
      // ignore
    }
  }
}
